import { useDate } from "vuetify";
import { useRouter } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { intakeService } from "@/services/intakeService";
import { DOCUMENT_CLASSIFICATIONS, IntakeDocument, IntakeOrder } from "@/models/intake";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import { useConfirmDialogStore } from "@/stores";

export function useIntakeActions() {
  const dateAdapter = useDate();
  const router = useRouter();
  const filtersStore = useIntakeListFiltersStore();
  const confirmDialogStore = useConfirmDialogStore();

  const onAddContent = async (page, documentType) => {
    if (documentType === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
      await intakeService.postCreateInsuranceCardFromPage(page.id);
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
      await intakeService.postCreateOrderFormFromPage(page.id);
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.TO_DO) {
      await intakeService.postCreateToDoFromPage(page.id);
    }
  };

  const contentTypeLabel = (documentType) => {
    if (documentType === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
      return "insurance card";
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
      return "order form";
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.TO_DO) {
      return "to do";
    }
    return "";
  };

  // Prompt for deletion confirmation only. Kept separate from the delete call so
  // callers can run it BEFORE any loading state, keeping the dialog interactive.
  const confirmDeleteContent = async (documentType) => {
    const contentType = contentTypeLabel(documentType);
    return await confirmDialogStore.confirm({
      title: `Delete ${contentType}`,
      message: `Are you sure you want to delete this ${contentType}?`,
      confirmText: "Delete",
      confirmColor: "error",
    });
  };

  const onDeleteContent = async (content, documentType) => {
    if (await confirmDeleteContent(documentType)) {
      await onDeleteContentConfirm(content, documentType);
    }
  };

  const onDeleteContentConfirm = async (content, documentType) => {
    if (documentType === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
      await intakeService.deleteInsuranceCard(content.id);
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
      await intakeService.deleteOrderForm(content.id);
    } else if (documentType === DOCUMENT_CLASSIFICATIONS.TO_DO) {
      await intakeService.deleteToDo(content.id);
    }
  };

  const onSnooze = async ({ orderId = null, documentKey = null, minutes }) => {
    let response = null;
    if (orderId) {
      response = await intakeService.postSnoozeOrder(orderId, minutes);
    } else if (documentKey) {
      response = await intakeService.postSnoozeDocument(documentKey, minutes);
    }
    if (minutes > 0) {
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    }
    if (documentKey) {
      return new IntakeDocument(response, dateAdapter);
    } else if (orderId) {
      return new IntakeOrder(response, dateAdapter);
    }
  };

  const onArchive = async ({ orderId = null, documentKey = null, reRouteOnSuccess = true }) => {
    if (orderId) {
      await intakeService.postArchiveOrder(orderId);
    } else if (documentKey) {
      await intakeService.postArchiveDocument(documentKey);
    }
    if (reRouteOnSuccess) {
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    }
    return true;
  };

  return {
    onAddContent,
    confirmDeleteContent,
    onDeleteContent,
    onDeleteContentConfirm,
    onSnooze,
    onArchive,
  };
}
